"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Button } from "@/components/ui/button";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
const milestones = [{
  year: "2023",
  title: "The Incorporation",
  description: "Brendon J Building Material Wholesalers Pty Ltd was officially incorporated on July 6, 2023. Our founder, Brendon James, envisioned a more efficient supply chain for the Australian construction sector."
}, {
  year: "2023",
  title: "Victorian Operations",
  description: "Establishing our headquarters in Swan Hill, Victoria, we focused on building a robust infrastructure to handle bulk wholesale orders. Our location serves as a strategic hub for regional and national distribution."
}, {
  year: "2023",
  title: "Industrial Network",
  description: "We forged critical partnerships with leading manufacturers of cement, steel, and electrical hardware. This foundation ensures that every product we supply meets rigorous Australian standards for structural integrity."
}, {
  year: "2024",
  title: "Nationwide Wholesale",
  description: "Today, we operate as a premier B2B supplier, delivering high-volume building materials across Australia. Our commitment remains fixed on providing reliable bulk supply solutions to commercial contractors."
}];
const MilestoneItem = ({
  item,
  index
}: {
  item: (typeof milestones)[0];
  index: number;
}) => {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <div data-section-id="146123" ref={ref} data-index={index} className={`relative pl-10 md:pl-14 pb-16 last:pb-0 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      {}
      <div className="absolute -left-[13px] top-1.5 w-6 h-6 rounded-sm bg-background border-2 border-primary shadow-[0_0_15px_rgba(var(--color-primary),0.2)] flex items-center justify-center z-10">
        <div className="w-2 h-2 rounded-sm bg-primary"></div>
      </div>

      <div className="space-y-4">
        <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-sm bg-accent text-foreground text-xs font-mono font-bold tracking-widest uppercase">
          <Sparkles className="w-3.5 h-3.5 text-ring" />
          {item.year}
        </span>
        <h3 className="text-2xl md:text-3xl font-serif font-bold tracking-tight text-foreground">
          {item.title}
        </h3>
        <p className="text-muted-foreground leading-[1.8] text-lg max-w-xl font-sans">
          {item.description}
        </p>
      </div>
    </div>;
};
export default function OurJourney() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>();
  const {
    ref: imageRef,
    isVisible: isImageVisible
  } = useRevealOnScroll<HTMLDivElement>();
  return <section id="our-foundation" className="relative bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      {}
      <div className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-overlay bg-[url(https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597043/img/faint_noise_texture.png)] bg-repeat z-0"></div>

      {}
      <div className="absolute top-0 left-1/4 w-[500px] h-[500px] bg-accent/10 rounded-full blur-[120px] pointer-events-none z-0"></div>
      <div className="absolute bottom-0 right-1/4 w-[600px] h-[600px] bg-muted/20 rounded-full blur-[150px] pointer-events-none z-0"></div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="grid lg:grid-cols-2 gap-16 lg:gap-24 items-start">
          
          {}
          <div className="order-2 lg:order-1">
            <div ref={headerRef} className={`mb-16 transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
              <h2 className="text-4xl md:text-5xl lg:text-7xl font-serif font-extrabold tracking-tighter mb-6">
                Our Foundation
              </h2>
              <p className="text-xl text-muted-foreground leading-[1.8] max-w-lg font-sans">
                A legacy built on structural integrity and reliable wholesale supply. Established in 2023, we provide the backbone for Australia's construction industry.
              </p>
            </div>

            {}
            <div className="relative border-l border-border/60 ml-3 md:ml-4">
              {milestones.map((item, i) => <MilestoneItem key={i} item={item} index={i} />)}
            </div>

            <div className="mt-16 ml-3 md:ml-4 pl-10 md:pl-14">
              <Button size="lg" className="rounded-sm px-8 h-14 text-base font-bold shadow-xl shadow-primary/10 hover:shadow-primary/20 transition-all uppercase tracking-wider" onClick={() => window.location.href = "/ProductsPage"}>
                View Bulk Inventory
                <ArrowRight className="ml-2 w-4 h-4" />
              </Button>
            </div>
          </div>

          {}
          <div className="order-1 lg:order-2 lg:sticky lg:top-32 h-full lg:h-auto">
            <div ref={imageRef} className={`relative rounded-xl overflow-hidden border border-border/50 shadow-2xl bg-card transition-all duration-1000 delay-300 ease-out ${isImageVisible ? "opacity-100 scale-100" : "opacity-0 scale-95"}`}>
              {}
              <div className="absolute inset-0 bg-gradient-to-t from-primary/80 via-primary/20 to-transparent z-10"></div>
              
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1597043/img/shopsphere_founders_process.png" alt="Brendon J Building Materials Wholesale Operations" className="w-full h-[500px] lg:h-[800px] object-cover grayscale-[0.3]" />
              
              {}
              <div className="absolute bottom-6 left-6 right-6 md:bottom-10 md:left-10 md:right-10 z-20">
                <div className="backdrop-blur-md bg-background/90 border border-border p-6 md:p-8 rounded-sm shadow-2xl">
                  <p className="text-foreground font-mono text-sm md:text-base leading-[1.8] tracking-tight">
                    "BRENDON J BUILDING MATERIAL WHOLESALERS PTY LTD. ACN: 669 488 386. Incorporated: July 6, 2023. Swan Hill, Victoria, Australia."
                  </p>
                  <div className="mt-4 flex items-center gap-3">
                    <div className="w-10 h-px bg-ring"></div>
                    <span className="text-muted-foreground text-xs font-mono uppercase tracking-widest">
                      Corporate Identity
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>

        </div>
      </div>
    </section>;
}